import React, { useContext, useState } from 'react';
import { useEffect } from 'react';
import { HubConnection } from '@microsoft/signalr';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { ConnectionState } from 'utils/connectionState';
import { getNegotiatedConnection, receiveNotifications,
} from '../Notifications/signalrConnector';
import { setGeneratedFormDocuments } from 'features/form/formReducer';
import { updateConnection } from 'store/notificationsReducer';
import { PdfProcessedEvent } from 'features/form/types';
import { CampaignUpdatedEvent } from 'Api/Campaigns/Types/types';
import { useCampaignStaleMarker } from 'hooks/useCampaignStaleMarker';
import { AuthContext } from '@urbanx/agx.auth.client';
import { HUB_NAMES } from 'constants/signalrConstants';

interface SetupNotificationsLayoutProps {
  children: React.ReactNode;
}

export const SetupNotificationsLayout = ({
  children,
}: SetupNotificationsLayoutProps) => {
  const [hubConnection, setHubConnection] = useState<HubConnection | null>(
    null
  );
  const [lockResolver, setLockResolver] = useState<
    ((value?: unknown) => void) | null
  >(null);
  const dispatch = useAppDispatch();

  const { userAccount, getAuthToken } = useContext(AuthContext);

  const { connectionState, connectionUpdatedAt } = useAppSelector(
    state => state.notifications
  );

  const markCampaignAsStale = useCampaignStaleMarker();

  // Add a lock to prevent the browser tab from sleeping while it isn't opened
  useEffect(() => {
    if (navigator && navigator.locks && navigator.locks.request) {
      const promise = new Promise(res => {
        setLockResolver(res);
      });

      navigator.locks.request(
        'autopilot_shared_lock',
        {
          mode: 'shared',
        },
        () => {
          return promise;
        }
      );
    }

    return () => {
      if (lockResolver) {
        lockResolver();
      }
    };
  }, []);

  useEffect(() => {
    if (connectionState === ConnectionState.Disconnected) {
      console.error('AutoPilot disconnected.');
    }
  }, [connectionState, connectionUpdatedAt]);

  useEffect(() => {
    if (hubConnection || !userAccount) return;

    dispatch(updateConnection(ConnectionState.Connecting));

    const hubCon = getNegotiatedConnection(HUB_NAMES.APP_NOTIFICATIONS, getAuthToken, () => {
      dispatch(updateConnection(ConnectionState.Connected));
    });

    hubCon.onclose(() => {
      dispatch(updateConnection(ConnectionState.Disconnected));
    });

    hubCon.onreconnecting(() => {
      dispatch(updateConnection(ConnectionState.Reconnecting));
    });

    hubCon.onreconnected(() => {
      dispatch(updateConnection(ConnectionState.Connected));
    });

    receiveNotifications(
      hubCon,
      'PdfProcessedEvent',
      (data: PdfProcessedEvent) => {
        dispatch(setGeneratedFormDocuments(data));
      }
    );

    receiveNotifications(
      hubCon,
      'CampaignUpdatedEvent',
      async (data: CampaignUpdatedEvent) => {
          await markCampaignAsStale(data.campaignId);
      }
    );

    dispatch(updateConnection(ConnectionState.Connected));

    setHubConnection(hubCon);
  }, [userAccount, hubConnection]);

  return children;
};
